/**
 * Analytics Service
 * Service for fetching company analytics data from backend API
 */
import { Observable } from 'rxjs';
import { get, post } from './smartApiService';

export interface PerformanceMetrics {
  current_price: number;
  day_change: number;
  day_change_percent: number;
  week_52_high: number;
  week_52_low: number;
  volume_today: number;
  volume_avg_30d: number;
  volume_avg_90d: number;
  beta: number | null;
  volatility_30d: number | null;
  volatility_category: string | null;
  market_cap: number | null;
  calculated_at: string;
}

export interface TechnicalIndicators {
  moving_averages: {
    sma_50: number | null;
    sma_200: number | null;
    ema_50: number | null;
    ema_200: number | null;
    ma_signal: string | null;
    ma_crossover: string | null;
  };
  rsi: {
    value: number | null;
    signal: string | null;
  };
  macd: {
    macd_line: number | null;
    signal_line: number | null;
    histogram: number | null;
    crossover: string | null;
  };
  bollinger_bands: {
    upper: number | null;
    middle: number | null;
    lower: number | null;
    position: number | null;
    signal: string | null;
  };
  support_resistance: {
    support_1: number | null;
    support_2: number | null;
    resistance_1: number | null;
    resistance_2: number | null;
    signal: string | null;
  };
  overall: {
    rating: string | null;
    score: number | null;
    buy_signals: number;
    sell_signals: number;
    neutral_signals: number;
  };
  calculated_at: string;
}

export interface VolumeMomentum {
  volume: {
    today: number | null;
    average: number | null;
    trend: string | null;
    signal: string | null;
  };
  vwap: {
    value: number | null;
    price_vs_vwap: number | null;
    signal: string | null;
  };
  delivery: {
    percent: number | null;
    category: string | null;
    signal: string | null;
  };
  momentum: {
    overall: string | null;
    direction: string | null;
    mfi: number | null;
    mfi_signal: string | null;
  };
  trading_date: string;
}

export interface DecisionSummaryData {
  overall: {
    signal: string;
    strength: number;
  };
  components: {
    technical: {
      score: number | null;
      signal: string | null;
    };
    valuation: {
      score: number | null;
      signal: string | null;
    };
    fundamental: {
      score: number | null;
      signal: string | null;
    };
    sentiment: {
      score: number | null;
      signal: string | null;
    };
    momentum: {
      score: number | null;
      signal: string | null;
    };
  };
  reasons: {
    buy: string;
    sell: string;
    hold: string;
  };
  analysis: {
    positives: string;
    negatives: string;
    risks: string;
  };
  metadata: {
    calculation_method: string;
    model_version: string;
    calculated_at: string;
    valid_until: string;
  };
}

export interface CompanyAnalytics {
  company: {
    id: number;
    symbol: string;
    name: string;
    sector_id: number | null;
    market_cap: number | null;
  };
  performance: PerformanceMetrics | null;
  technical: {
    overall_rating: string | null;
    rating_score: number | null;
    rsi: number | null;
    rsi_signal: string | null;
    ma_signal: string | null;
    support_level_1: number | null;
    resistance_level_1: number | null;
  } | null;
  volume: {
    overall_momentum: string | null;
    momentum_direction: string | null;
    vwap: number | null;
    delivery_percent: number | null;
    delivery_signal: string | null;
  } | null;
  decision: {
    overall_signal: string | null;
    signal_strength: number | null;
    technical_signal: string | null;
    momentum_signal: string | null;
  } | null;
  last_updated: {
    performance: string | null;
    technical: string | null;
    volume: string | null;
    decision: string | null;
  };
}

export const analyticsService = {
  /**
   * Get all analytics data for a company
   */
  getAllAnalytics(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics`, { showLoading: false });
  },

  /**
   * Get performance metrics for a company
   */
  getPerformanceMetrics(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/performance`, { showLoading: false });
  },

  /**
   * Get technical indicators for a company
   */
  getTechnicalIndicators(companyId: number, timeframe: string = '1d'): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/technical?timeframe=${timeframe}`, { showLoading: false });
  },

  /**
   * Get volume and momentum data for a company
   */
  getVolumeMomentum(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/volume`, { showLoading: false });
  },

  /**
   * Get decision summary for a company
   */
  getDecisionSummary(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/decision`, { showLoading: false });
  },

  /**
   * Refresh analytics data for a company
   */
  refreshAnalytics(companyId: number, forceRecalculate: boolean = false): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh?force_recalculate=${forceRecalculate}`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Refresh only performance metrics for a company
   */
  refreshPerformanceMetrics(companyId: number): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh/performance`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Refresh only technical indicators for a company
   */
  refreshTechnicalIndicators(companyId: number): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh/technical`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Refresh only volume and momentum metrics for a company
   */
  refreshVolumeMomentum(companyId: number): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh/volume`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Refresh only fundamentals data for a company
   */
  refreshFundamentals(companyId: number): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh/fundamentals`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Refresh only news and sentiment data for a company
   */
  refreshNewsSentiment(companyId: number): Observable<any> {
    return post(
      `/api/companies/${companyId}/analytics/refresh/news`,
      {},
      { showLoading: true }
    );
  },

  /**
   * Get weekly analytics for a company
   */
  getWeeklyAnalytics(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/weekly`, { showLoading: false });
  },

  /**
   * Get monthly analytics for a company
   */
  getMonthlyAnalytics(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/monthly`, { showLoading: false });
  },

  /**
   * Get quarterly analytics for a company
   */
  getQuarterlyAnalytics(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/analytics/quarterly`, { showLoading: false });
  },

  /**
   * Get analytics summary for multiple companies
   */
  getCompaniesSummary(params: {
    limit?: number;
    offset?: number;
    sector_id?: number;
    sort_by?: string;
  } = {}): Observable<any> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.sector_id) queryParams.append('sector_id', params.sector_id.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);

    const queryString = queryParams.toString();
    const url = `/api/companies/analytics/summary${queryString ? '?' + queryString : ''}`;

    return get(url, { showLoading: false });
  },

  /**
   * Get news and sentiment data for a company using AI
   */
  getNewsSentiment(companyId: number): Observable<any> {
    return get(`/api/companies/${companyId}/news-sentiment`, { showLoading: false });
  }
};

export default analyticsService;
